import { useReducer } from "react";
import ZoneTypeContext, { internalState } from "./context";
import reducer from "./reducer";
import { execute } from "../../../utils/helpers/execute";
import EndPoints from "../../../services/end-points";
import { IZoneType, IZoneTypeDetails } from "../../../models/wms/zone-type/response";
import {
  ICreateZoneType,
  IUpdateZoneType,
} from "../../../models/wms/zone-type/request";
import { IZoneTypeQuery } from "../../../models/wms/zone-type/query";

export interface IProps {
  children: React.ReactNode;
}

const ZoneTypeContextProvider: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, internalState);

  const getData = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });

        const { data } = await EndPoints.wmsLayout.getAllZoneTypes(state.query);

        dispatch({ type: "SET_LIST", payload: { list: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };

  const getDetails = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });

        const { data } = await EndPoints.wmsLayout.getZoneType(id);
        dispatch({ type: "SET_DETAILS", payload: { details: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });
      },
      throwException: false,
    });
  };

  const setDetails = async (data?: IZoneTypeDetails) => {
    dispatch({ type: "SET_DETAILS", payload: { details: data } });
  };

  const createZoneType = async (request: ICreateZoneType) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });

        await EndPoints.wmsLayout.createZoneType(request);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });
      },
      throwException: true,
    });
  };

  const updateZoneType = async (id: number, request: IUpdateZoneType) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
        await EndPoints.wmsLayout.updateZoneType(id, request);

        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const deleteZoneType = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });

        await EndPoints.wmsLayout.deleteZoneType(id);

        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });
      },
      throwException: true,
    });
  };

  const setSearch = (search?: string) => {
    dispatch({ type: "SET_SEARCH", payload: { search } });
  };

  const setQuery = (query: IZoneTypeQuery) => {
    dispatch({ type: "SET_QUERY", payload: { query } });
  };

  return (
    <ZoneTypeContext.Provider
      value={{
        ...state,
        actions: {
          getData,
          getDetails,
          setDetails,
          createZoneType,
          updateZoneType,
          deleteZoneType,
          setSearch,
          setQuery,
        },
      }}
    >
      {props.children}
    </ZoneTypeContext.Provider>
  );
};

export default ZoneTypeContextProvider;
